import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import { Review } from '@model/iceberg/booking/package';
import { withTheme } from '@emotion/react';
import React from 'react';
import { Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { TextDescriptionFade } from '@components/common/description';
import { SkeletonElement, SkeletonSize, SkeletonVariant } from '@components/common/skeleton-loading';
import { mixins } from '@styles/mixins';

/* ***************** *
 *       Types       *
 * ***************** */
export interface ProductReviewProps {
  theme?: Theme;
  testId?: string;
  review: Review;
  isGhosting?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div(({ theme }: ThemeProps) => ({
  ...mixins(theme).colourCard,
  marginBottom: theme.custom.spacing.xLarge,

  ['&:last-of-type']: {
    marginBottom: 0
  }
}));

const TopRowContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginLeft: -14
});

const ReviewTitle = styled.h6(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h6 as any),
  fontWeight: 'bold',
  fontSize: theme.custom.fontSize.medium,
  marginTop: theme.custom.spacing.large,
  marginBottom: 0
}));

/* **************************** *
 *    ProductReviewComponent    *
 * **************************** */

export const ProductReviewComponent = (props: ProductReviewProps) => {
  const t: any = useI18NextContext();
  const {
    isGhosting,
    review: { text, title, ratingImageUrl, user },
    theme
  } = props;

  if (isGhosting) {
    return (
      <Container data-testid={props.testId || TestId.productReview.main}>
        <TopRowContainer>
          <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={20} height={20} repeat={5} inline />
        </TopRowContainer>
        <ReviewTitle>
          <SkeletonElement variant={SkeletonVariant.HEADING} size={SkeletonSize.SMALL} />
        </ReviewTitle>
        <SkeletonElement />
        <SkeletonElement repeat={5} />
      </Container>
    );
  }

  return (
    <Container data-testid={props.testId || TestId.productReview.main}>
      <TopRowContainer>
        <img
          data-testid={TestId.productDescription.tripAdvisorRating}
          src={ratingImageUrl}
          alt={t('alt__product-description--tripadvisor')}
        />
      </TopRowContainer>
      <ReviewTitle>{title}</ReviewTitle>
      <TextDescriptionFade
        description={text}
        initialHeight={65}
        user={user}
        fadeColour={theme?.custom.colors.group6.lighter}
      />
    </Container>
  );
};

export const ProductReview = withTheme(ProductReviewComponent);
